import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, TextField, Button, MenuItem } from '@mui/material';
import { useAuth } from '../provider/auth';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import { toast } from 'react-toastify';
import { FaEnvelope, FaPhone, FaPaperPlane } from 'react-icons/fa';

const Contact = () => {
    const { user } = useAuth();
    const [boardMembers, setBoardMembers] = useState([]);
    const [topics, setTopics] = useState([]);
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ topic: '', message: '' });

    const API_BASE = `${API_BASE_URL}/api`;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${API_BASE}/contact/data`);
                setBoardMembers((res.data.members || []).sort((a, b) => a.order - b.order));
                setTopics(res.data.topics || []);
                setSettings(res.data.settings || {});
                if (res.data.topics?.length > 0) {
                    setFormData(prev => ({ ...prev, topic: res.data.topics[0].value }));
                }
            } catch (err) {
                console.error('Error fetching contact data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_BASE]);

    const getDisplayName = (m) => {
        if (!m) return '';
        if (m.playerId && typeof m.playerId === 'object') {
            if (m.playerId.firstName || m.playerId.lastName) {
                return `${m.playerId.firstName || ''} ${m.playerId.lastName || ''}`.trim();
            }
        }
        return m.name || '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const selectedTopicLabel = topics.find(t => t.value === formData.topic)?.label;
            
            await axios.post(`${API_BASE}/contact/send-email`, {
                topic: formData.topic,
                message: formData.message,
                senderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name,
                senderEmail: user?.email
            }, { withCredentials: true });

            toast.success(`Deine Nachricht zum Thema "${selectedTopicLabel}" wurde gesendet.`);
            setFormData(prev => ({ ...prev, message: '' }));
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Fehler beim Senden der E-Mail.';
            toast.error(errorMsg);
        }
    };

    if (loading) return <div className="p-5 text-center">Lädt...</div>;

    const heroImage = settings.boardImagePath?.startsWith('uploads') 
        ? `${API_BASE_URL}/${settings.boardImagePath}`
        : null; // No default image

    return (
        <Box>
            {/* Hero Section - only show if image exists */}
            {heroImage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5, maxHeight: 500, overflow: 'hidden' }}>
                    <Box 
                        component="img"
                        src={heroImage} 
                        alt="Vorstand" 
                        sx={{ 
                            maxWidth: '100%', 
                            height: 'auto', 
                            maxHeight: 500, 
                            objectFit: 'contain',
                            borderRadius: '16px',
                            boxShadow: 3
                        }} 
                    />
                </Box>
            )}

            <Box>
                {/* Board Group */}
                <Box sx={{ mb: 5 }}>
                    <Grid container spacing={3} justifyContent="center">
                        {boardMembers.map((member, mIdx) => (
                            <Grid item key={mIdx} xs={6} sm={6} md={4} lg={3}>
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        height: '100%', 
                                        p: 3, 
                                        textAlign: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                                        {getDisplayName(member)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {member.position}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                        {member.email && (
                                            <Box 
                                                component="a" 
                                                href={`mailto:${member.email}`} 
                                                title={member.email} 
                                                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' }}
                                            >
                                                <FaEnvelope size={18} />
                                            </Box>
                                        )}
                                        {member.phone && (
                                            <Box 
                                                component="a" 
                                                href={`tel:${member.phone}`} 
                                                title={member.phone} 
                                                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' }, transition: 'color 0.2s' }}
                                            >
                                                <FaPhone size={18} />
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Bottom Section: Centered Form */}
                <Grid container justifyContent="center" sx={{ mt: 5 }}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 4, 
                                borderRadius: '12px', 
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <Typography variant="h5" color="primary" textAlign="center" sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <FaPaperPlane color="#d32f2f" /> Schick uns eine Nachricht
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                                Hallo <strong>{user?.firstName || 'Mitglied'}</strong>, wie können wir dir helfen?
                            </Typography>
                            
                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Thema"
                                    value={formData.topic}
                                    onChange={(e) => setFormData({...formData, topic: e.target.value})}
                                    sx={{ mb: 3 }}
                                    size="small"
                                >
                                    {topics.map(t => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={5}
                                    label="Nachricht"
                                    placeholder="Deine Nachricht an den Vorstand..."
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    required
                                    sx={{ mb: 1 }}
                                />

                                {(() => {
                                    const selectedTopic = topics.find(t => t.value === formData.topic);
                                    if (selectedTopic?.hint) {
                                        return (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, fontStyle: 'italic' }}>
                                                <strong>Hinweis:</strong> {selectedTopic.hint}
                                            </Typography>
                                        );
                                    }
                                    return <Box sx={{ mb: 3 }} />;
                                })()}

                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    type="submit" 
                                    fullWidth 
                                    sx={{ 
                                        borderRadius: '24px', 
                                        py: 1.5, 
                                        fontWeight: 'bold',
                                        boxShadow: 2
                                    }}
                                >
                                    Absenden
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Contact;
  
